/**
 * An artifact beside the chat: its title, any of its versions, sharing, and
 * "Change this…", which sends a message in the chat asking for the change
 * (`TurnRequest.artifact`), as the Gallery's revisions do for an image. The
 * reply's change is a new version, and the panel moves to it.
 *
 * In design mode it goes before the chat and takes most of the width
 * (`first`); otherwise it sits after it, wider than the drafts panel for a
 * page.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ApiError,
  designSystemFromArtifact,
  getArtifact,
  getArtifactVersion,
  type Artifact,
  type ArtifactSummary,
  type ArtifactVersion,
  type DesignSystemCreate
} from '../api';
import { ds } from '../ds';
import { Link } from '../router';
import { framed } from '../artifactFrame';
import { exportable, saveFile, sourceFile, standaloneFile } from '../artifactExport';
import { PanelFrame, PanelTitle } from './ArtifactPanel';
import { ArtifactView } from './ArtifactView';
import { ShareDialog } from './ShareDialog';
import { SystemDialog } from './settings/DesignTab';
import { IconTap, Menu, MenuItem, useDismiss } from './ui';

const FAILED = 'That did not go through. Try again.';

function ChangeBox({ busy, onSend }: { busy: boolean; onSend: (text: string) => void }) {
  const [text, setText] = useState('');
  const { Button } = ds();
  const send = () => {
    const t = text.trim();
    if (!t || busy) return;
    onSend(t);
    setText('');
  };
  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <textarea
        aria-label="Change this artifact"
        placeholder="Change this…"
        value={text}
        rows={2}
        maxLength={4_000}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !matchMedia('(pointer: coarse)').matches) {
            e.preventDefault();
            send();
          }
        }}
        style={{
          border: 0,
          outline: 'none',
          resize: 'none',
          background: 'transparent',
          font: 'inherit',
          fontSize: 14,
          lineHeight: 1.5,
          color: 'var(--text-strong)',
          padding: '4px 6px 0'
        }}
      />
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ flex: 1 }} />
        <Button variant="accent" density="app" size="sm" iconLeft="paint-brush" disabled={!text.trim() || busy} onClick={send}>
          Change
        </Button>
      </div>
    </div>
  );
}

export function DesignPanel({
  artifactId,
  known,
  chatId,
  others,
  onPick,
  tabs,
  first,
  busy,
  onChange,
  onClose,
  overlay,
  narrow
}: {
  artifactId: string;
  /** The newest version the chat has seen made: a later one than the panel
   *  holds is fetched, and shown. */
  known: number;
  chatId: string;
  /** The chat's artifacts, to move between them. */
  others: readonly ArtifactSummary[];
  onPick: (id: string) => void;
  tabs?: ReactNode;
  first: boolean;
  /** A reply is running: a change waits until it ends. */
  busy: boolean;
  onChange: (text: string, artifact: { id: string; title: string }) => void;
  onClose: () => void;
  overlay: boolean;
  narrow: boolean;
}) {
  const [artifact, setArtifact] = useState<Artifact | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  /** The version shown; null for the latest. */
  const [shown, setShown] = useState<number | null>(null);
  const [older, setOlder] = useState<ArtifactVersion | null>(null);
  const [sharing, setSharing] = useState(false);
  const [menu, setMenu] = useState(false);
  /** The look read from this artifact, shown for review before it is saved. */
  const [lookDraft, setLookDraft] = useState<DesignSystemCreate | null>(null);
  const [note, setNote] = useState<ReactNode>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useDismiss(menuRef, menu, () => setMenu(false));

  useEffect(() => {
    let gone = false;
    setFailed(false);
    getArtifact(artifactId).then(
      (a) => {
        if (gone) return;
        setArtifact(a);
        setShown(null);
      },
      () => !gone && setFailed(true)
    );
    return () => {
      gone = true;
    };
  }, [artifactId, known, attempt]);

  useEffect(() => {
    setOlder(null);
    if (!artifact || shown === null || shown === artifact.version) return;
    let gone = false;
    getArtifactVersion(artifact.id, shown).then(
      (v) => !gone && setOlder(v),
      () => !gone && setFailed(true)
    );
    return () => {
      gone = true;
    };
  }, [artifact, shown]);

  const current = artifact && artifact.id === artifactId ? artifact : null;
  const version = current ? (shown === null || shown === current.version ? current.latest : older) : null;
  const latest = !!current && (shown === null || shown === current.version);

  const saveLook = () => {
    if (!current) return;
    setMenu(false);
    setNote('Reading its look…');
    designSystemFromArtifact(current.id).then(
      (read) => {
        setNote(null);
        setLookDraft(read);
      },
      (err: unknown) => setNote(err instanceof ApiError && err.status !== 0 ? err.message : FAILED)
    );
  };

  const copySource = () => {
    setMenu(false);
    if (!current || !version) return;
    void navigator.clipboard.writeText(version.content).then(
      () => setNote('Copied.'),
      () => setNote(FAILED)
    );
  };
  const download = () => {
    setMenu(false);
    if (current && version) saveFile(sourceFile(current.kind, current.title, version.content));
  };
  const exportFile = () => {
    setMenu(false);
    if (!current || !version) return;
    void standaloneFile(current.kind, current.title, version.content, version.tokens).then(saveFile, () => setNote(FAILED));
  };

  const head = (
    <>
      {tabs ?? (
        others.length > 1 ? (
          <select
            aria-label="Artifact"
            value={artifactId}
            onChange={(e) => onPick(e.target.value)}
            style={{ flex: 1, minWidth: 0, height: 30, border: 0, background: 'transparent', font: 'inherit', fontWeight: 600, fontSize: 14, color: 'var(--text-strong)' }}
          >
            {others.map((a) => (
              <option key={a.id} value={a.id}>
                {a.title}
              </option>
            ))}
          </select>
        ) : (
          <PanelTitle>{current?.title ?? ''}</PanelTitle>
        )
      )}
      {current && current.versions.length > 1 ? (
        <select
          aria-label="Version"
          value={String(shown ?? current.version)}
          onChange={(e) => setShown(Number(e.target.value))}
          style={{ flex: 'none', height: 30, border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-chrome)', background: 'transparent', font: 'inherit', fontSize: 12.5, color: 'var(--text-body)', padding: '0 4px' }}
        >
          {[...current.versions].reverse().map((v) => (
            <option key={v.version} value={v.version}>
              {v.version === current.version ? `Version ${v.version}, latest` : `Version ${v.version}`}
            </option>
          ))}
        </select>
      ) : null}
      {current ? (
        <IconTap icon="share-network" label="Share" active={current.share.level !== 'private'} onClick={() => setSharing(true)} />
      ) : null}
      {current && version ? (
        <div ref={menuRef} style={{ position: 'relative', flex: 'none' }}>
          <IconTap icon="dots-three" label="More" active={menu} onClick={() => setMenu((m) => !m)} />
          {menu ? (
            <Menu style={{ right: 0, top: 'calc(100% + 6px)', width: 260 }}>
              <MenuItem icon="copy" label="Copy source" onSelect={copySource} />
              <MenuItem icon="download-simple" label="Download source" onSelect={download} />
              {exportable(current.kind, version.tokens) ? (
                <MenuItem icon="export" label={current.kind === 'svg' ? 'Export with its tokens' : 'Export as a page'} onSelect={exportFile} />
              ) : null}
              {framed(current.kind) ? <MenuItem icon="palette" label="Save its look as a design system" onSelect={saveLook} /> : null}
            </Menu>
          ) : null}
        </div>
      ) : null}
    </>
  );

  return (
    <PanelFrame
      head={head}
      onClose={onClose}
      overlay={overlay}
      narrow={narrow}
      first={first}
      width={current && framed(current.kind) ? 'min(46%, 720px)' : 400}
    >
      {tabs && current ? <span style={{ padding: '10px 16px 0', fontWeight: 600, fontSize: 14, color: 'var(--text-strong)' }}>{current.title}</span> : null}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {current && version ? (
          <ArtifactView id={current.id} kind={current.kind} content={version.content} tokens={version.tokens} title={current.title} version={version.version} unsafe={version.unsafe} />
        ) : failed ? (
          <div style={{ padding: 16, fontSize: 13.5, color: 'var(--text-muted)' }}>
            This artifact did not load.{' '}
            <button type="button" className="bare" onClick={() => setAttempt((n) => n + 1)} style={{ fontSize: 13.5, color: 'var(--text-link)' }}>
              Retry
            </button>
          </div>
        ) : null}
      </div>
      {note ? <div style={{ flex: 'none', padding: '8px 16px 0', fontSize: 13, color: 'var(--text-muted)' }}>{note}</div> : null}
      {current && latest && current.chatId === chatId ? (
        <div style={{ flex: 'none', padding: 10 }}>
          <ChangeBox busy={busy} onSend={(text) => onChange(text, { id: current.id, title: current.title })} />
        </div>
      ) : null}
      {lookDraft ? (
        <SystemDialog
          system={null}
          draft={{ name: lookDraft.name, tokens: lookDraft.tokens, guidance: lookDraft.guidance ?? '' }}
          onSaved={(saved) =>
            setNote(
              <>
                Saved as {saved.name}.{' '}
                <Link to="/settings/design" style={{ color: 'var(--text-link)' }}>
                  Design systems
                </Link>
              </>
            )
          }
          onDeleted={() => undefined}
          onClose={() => setLookDraft(null)}
        />
      ) : null}
      {sharing && current ? (
        <ShareDialog
          artifact={current}
          shownVersion={version?.version ?? current.version}
          onSaved={(share) => setArtifact((a) => (a && a.id === current.id ? { ...a, share } : a))}
          onClose={() => setSharing(false)}
        />
      ) : null}
    </PanelFrame>
  );
}
